import { Flex } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import type { Board } from '@/components/Molecules/FaceliftDraggable/FaceliftDraggable';
import FaceliftDraggable from '@/components/Molecules/FaceliftDraggable/FaceliftDraggable';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import {
	useGetUserProfileClinics,
	useUpdateUserProfileClinics,
} from '@/hooks/service-hooks/users';
import { selectUser, useAuthStore } from '@/store/authStore';
import { useClinicStore } from '@/store/clinicStore';
import type { ClinicInput } from '@/types/clinic';

const Clinics = () => {
	const user = useAuthStore(selectUser);
	const authStore = useAuthStore.getState();
	const clinicStore = useClinicStore.getState();
	const userId = user?.userInformation?._id || '';

	const [clinicsData, setClinicsData] = useState<Board>({
		item: {},
		columns: {
			'column-1': {
				id: 'column-1',
				title: 'Available Clinics',
				itemIds: [],
			},
			'column-2': {
				id: 'column-2',
				title: 'Current Clinics',
				itemIds: [],
			},
		},
		columnOrder: ['column-1', 'column-2'],
	});

	const { data: fetchedClinics, isFetching: isClinicsFetching } =
		useGetUserProfileClinics(userId);

	const [errorMessage, setErrorMessage] = useState('');

	const {
		mutate: updateClinics,
		isLoading: isSubmitting,
		isSuccess: updateSuccess,
		isError: updateError,
	} = useUpdateUserProfileClinics(
		userId,
		(response) => {
			const { data } = response;
			authStore.updateUserPreferences({
				clinicPreferences: data.clinicPreferences,
			});
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			setErrorMessage(
				apiError?.response?.data?.message ||
					'Failed to update clinics. Please try again.',
			);
		},
	);

	useEffect(() => {
		if (fetchedClinics?.data) {
			const item: Record<string, { id: string; content: string }> = {};
			const availableItemIds: string[] = [];
			const currentItemIds: string[] = [];

			fetchedClinics.data.availableClinics?.forEach((clinic: ClinicInput) => {
				item[clinic.id] = {
					id: clinic.id,
					content: clinic.name,
				};
				availableItemIds.push(clinic.id);
			});

			fetchedClinics.data.currentClinics?.forEach((clinic: ClinicInput) => {
				if (!item[clinic.id]) {
					item[clinic.id] = {
						id: clinic.id,
						content: clinic.name,
					};
				}
				currentItemIds.push(clinic.id);
			});

			authStore.updateUserPreferences({
				currentClinics: fetchedClinics.data.currentClinics,
			});

			const allClinics = [...(fetchedClinics.data.availableClinics || []), ...(fetchedClinics.data.currentClinics || [])];
			clinicStore.setClinics(fetchedClinics.data.currentClinics);
			clinicStore.setRawClinics(allClinics);

			setClinicsData((prev) => ({
				...prev,
				item,
				columns: {
					...prev.columns,
					'column-1': {
						...prev.columns['column-1'],
						itemIds: availableItemIds,
					},
					'column-2': {
						...prev.columns['column-2'],
						itemIds: currentItemIds,
					},
				},
			}));
		}
	}, [fetchedClinics]);

	const handleSubmit = () => {
		if (clinicsData.columns['column-2'].itemIds.length === 0) {
			return toast.error(
					'Please select at least one clinic before updating.',
				);
		}
		const currentClinics = clinicsData.columns['column-2'].itemIds;
		updateClinics(currentClinics);
	};

	const isBothColumnsEmpty =
		clinicsData.columns['column-1'].itemIds.length === 0 &&
		clinicsData.columns['column-2'].itemIds.length === 0;

	return (
		<>
			<FaceliftSuccessProgressModal
				open={
					isSubmitting ||
					updateSuccess ||
					updateError
				}
				isLoading={isSubmitting}
				isSucceed={updateSuccess}
				hasError={updateError}
				onOpenChange={() => {}}
				title="Clinics Updated!"
				errorMessage={
					updateError
						? errorMessage || 'Error updating clinics'
						: ''
				}
			/>
			<Flex gap={4} flexDirection={'column'}>
				<FaceliftDraggable
					data={clinicsData}
					onChange={setClinicsData}
					isLoading={isClinicsFetching}
					height="250px"
				/>

				<Flex justify="flex-end" mt={4}>
					<FaceliftButton
						width="250px"
						size="md"
						onClick={handleSubmit}
						loading={isSubmitting}
						disabled={isSubmitting || isBothColumnsEmpty}
					>
						Update
					</FaceliftButton>
				</Flex>
			</Flex>
		</>
	);
};

export default Clinics;
